import React, { useState, useEffect, useRef } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useNavigate } from "react-router-dom";
import TableDataNoAvailable from "../components/ui/TableDataNoAvailable";
import LoadingComponent from "../components/ui/LoadingComponent";
import useDebounce from "../hook/useDebounce";
import Breadcrumb from "../components/Breadcrumb";
import { formatDateTime } from "../utils/formatDateTime";
import PaginationControls from "../components/ui/PaginationControls";
import { usePermissions } from "../context/PermissionContext";
import CardContainer from "../components/ui/CardContainer";
import PerPageDropdown from "../components/ui/PerPageDropdown";
import ErrorAlert from "../components/ui/ErrorAlert";
import SortIcons from "../components/ui/SortIcons";
import StatusBadge from "../components/ui/StatusBadge";
import { AddButton } from "../components/ui/Button";
import { staffApi } from "../Apis/Api";
import InfoModal from "../components/ui/InfoeModal";

const IncentivePlan = () => {
  const navigate = useNavigate();
  const [incentivePlans, setIncentivePlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    perPage: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({
    search: "",
    status: "",
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [planToDelete, setPlanToDelete] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    field: null,
    direction: "asc",
  });
  const [showInfoModal, setShowInfoModal] = useState(false); // 👈 NEW: Info Modal State
  const searchInputRef = useRef(null);
  const { hasPermission } = usePermissions();

  useEffect(() => {
    if (!loading && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [loading]);

  const debouncedSearch = useDebounce(filters.search, 500);

  useEffect(() => {
    fetchIncentivePlans();
  }, [
    pagination.currentPage,
    pagination.perPage,
    debouncedSearch,
    filters.status,
    sortConfig,
  ]);

  const fetchIncentivePlans = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: pagination.currentPage,
        per_page: pagination.perPage,
        search: debouncedSearch,
        sort_by: sortConfig.field,
        sort_order: sortConfig.direction,
      };
      if (filters.status) {
        params.status = filters.status === "Active" ? "true" : "false";
      }

      const response = await staffApi.getAllIncentivePlans(params);
      console.log("Incentive Plans API Response:", response);

      const data = response.data || [];

      // Debug: Check if IDs are coming from API
      console.log("Raw API data (first item):", data[0]);

      // Map API response with better ID handling
      const formattedData = data.map((plan, index) => {
        // Check multiple possible ID fields
        const planId =
          plan.id || plan.incentive_plan_id || plan.plan_id || null;

        return {
          id: planId,
          plan_name: plan.plan_name || "-",
          plan_type: plan.plan_type || "-",
          period_type: plan.period_type || "-",
          value_type: plan.value_type || "-",
          fixed_value: plan.fixed_value || "-",
          percent_value: plan.percent_value || "-",
          currency_code: plan.currency_code || "-",
          min_redeem_value: plan.min_redeem_value || "-",
          is_active:
            plan.is_active !== undefined && plan.is_active !== null
              ? plan.is_active
                ? "Active"
                : "Inactive"
              : "-",
          created_at: plan.created_at || "-",
          display_created_at: plan.created_at
            ? formatDateTime(plan.created_at)
            : "-",
          client_name: plan.client_name || "-",
          created_by_name: plan.created_by_name || "-",
        };
      });

      // Filter out plans without valid IDs
      const validPlans = formattedData.filter(
        (plan) => plan.id !== null && plan.id !== undefined
      );
      const invalidPlans = formattedData.filter(
        (plan) => plan.id === null || plan.id === undefined
      );

      if (invalidPlans.length > 0) {
        console.error("Plans with missing IDs:", invalidPlans);
        setError(
          `${invalidPlans.length} plans have missing IDs. Please check the API response.`
        );
      }

      setIncentivePlans(validPlans);
      setPagination((prev) => ({
        ...prev,
        total: response.pagination?.total || validPlans.length,
      }));

      if (invalidPlans.length === 0) {
        setError(null);
      }
    } catch (err) {
      console.error("Fetch error:", err);
      setError(err.message || "Failed to fetch incentive plans.");
      setIncentivePlans([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePerPageChange = (value) => {
    setPagination((prev) => ({ ...prev, perPage: value, currentPage: 1 }));
  };

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, currentPage: page }));
  };

  const handleSearchChange = (e) => {
    setFilters((prev) => ({ ...prev, search: e.target.value }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleStatusFilterChange = (status) => {
    setFilters((prev) => ({
      ...prev,
      status: status === "Select Status" ? "" : status,
    }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleAssignIncentive = (plan) => {
    navigate("/add-incentive-assgiment", {
      state: {
        stateData: {
          incentive_plan_id: plan.id,
          plan_name: plan.plan_name,
        },
      },
    });
  };

  const handleEdit = (plan) => {
    console.log("Editing plan:", plan);
    navigate("/edit-incentive-plan", { state: { staffData: plan } });
  };

  const handleViewDetails = (plan) => {
    navigate("", { state: { staffData: plan } });
  };

  const handleDeleteClick = (planId, planName) => {
    console.log("Plan to delete:", { id: planId, name: planName });

    if (
      !planId ||
      planId === null ||
      planId === undefined ||
      String(planId).startsWith("temp_")
    ) {
      setError(`Cannot delete plan "${planName}". Invalid or missing plan ID.`);
      return;
    }

    setPlanToDelete({ id: planId, name: planName });
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (planToDelete && planToDelete.id) {
      if (String(planToDelete.id).startsWith("temp_")) {
        setError(
          "Cannot delete plan with temporary ID. Please refresh the page and try again."
        );
        setShowDeleteModal(false);
        setPlanToDelete(null);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        console.log("Deleting plan ID:", planToDelete.id);

        const response = await staffApi.deleteIncentivePlan(planToDelete.id);
        console.log("Delete response:", response);

        setIncentivePlans((prev) =>
          prev.filter((plan) => plan.id !== planToDelete.id)
        );

        setPagination((prev) => ({
          ...prev,
          total: prev.total - 1,
          currentPage: Math.min(
            prev.currentPage,
            Math.ceil((prev.total - 1) / prev.perPage) || 1
          ),
        }));

        setShowDeleteModal(false);
        setPlanToDelete(null);
      } catch (err) {
        console.error("Delete error:", err);

        let errorMessage = "Failed to delete incentive plan.";

        if (
          err.message &&
          err.message.includes("Invalid text representation")
        ) {
          errorMessage =
            "Invalid plan ID. Please refresh the page and try again.";
        } else if (err.message === "Plan not found or unauthorized") {
          errorMessage =
            "Plan not found or you don't have permission to delete this plan.";
        } else if (err.message) {
          errorMessage = err.message;
        }

        setError(errorMessage);
        setShowDeleteModal(false);
        setPlanToDelete(null);
      } finally {
        setLoading(false);
      }
    } else {
      setError("No valid plan selected for deletion.");
      setShowDeleteModal(false);
      setPlanToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setPlanToDelete(null);
  };

  const handleSort = (field) => {
    setSortConfig((prev) => ({
      field,
      direction:
        prev.field === field && prev.direction === "asc" ? "desc" : "asc",
    }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  // 👈 NEW: Info Modal Handlers
  const handleInfoClick = () => {
    setShowInfoModal(true);
  };

  const handleInfoClose = () => {
    setShowInfoModal(false);
  };

  if (error && incentivePlans.length === 0) {
    return <ErrorAlert error={error} onRetry={fetchIncentivePlans} />;
  }

  const headerContent = (
    <div className="container-fluid">
      <div className="row align-items-center">
        <div className="col-12 col-md-6 mb-3 mb-md-0">
          <div className="d-flex align-items-center flex-wrap gap-3">
            <div className="navbar-search flex-grow-1">
              <input
                type="text"
                className="form-control h-40-px w-100"
                name="search"
                placeholder="Search by Plan Name"
                value={filters.search}
                onChange={handleSearchChange}
                ref={searchInputRef}
                aria-label="Search incentive plans by name, type, or client"
              />
              <i className="bi bi-search icon"></i>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-6 d-flex align-items-center justify-content-between">
          <PerPageDropdown
            perPage={pagination.perPage}
            handlePerPageChange={handlePerPageChange}
          />
          <div className="d-flex align-items-center gap-2">
            {" "}
            {/* 👈 UPDATED: Added Info Button */}
            <button
              type="button"
              className="bg-info-50 text-info-600 bg-hover-info-100 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              title="Information about the Incentive Plan List Page"
              onClick={handleInfoClick}
              aria-label="Page Information"
            >
              <Icon icon="lucide:info" className="menu-icon" />
            </button>
            <AddButton
              label="Add Plan"
              permissionKey="add_incentive"
              onClick={() => navigate("/add-incentive-plan")}
            />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Breadcrumb title="Incentive Plan List" />

      {error && incentivePlans.length > 0 && (
        <div className="mb-3">
          <div className="alert alert-warning" role="alert">
            <strong>Warning:</strong> {error}
          </div>
        </div>
      )}

      <CardContainer
        headerContent={headerContent}
        paginationControls={
          <PaginationControls
            pagination={pagination}
            handlePageChange={handlePageChange}
          />
        }
        loading={loading}
      >
        <div
          className="position-relative"
          style={{
            opacity: loading ? 0.4 : 1,
            pointerEvents: loading ? "none" : "auto",
          }}
        >
          {incentivePlans.length === 0 && !loading ? (
            <TableDataNoAvailable
              message={
                filters.search || filters.status
                  ? "No incentive plans match your search or filter."
                  : "No incentive plans available."
              }
            />
          ) : (
            <table className="table bordered-table sm-table mb-0">
              <thead>
                <tr>
                  <th scope="col">S.No</th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("plan_name")}
                    role="button"
                    aria-label={`Sort by plan name, currently sorted ${
                      sortConfig.field === "plan_name"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Plan Name
                      <SortIcons field="plan_name" sortConfig={sortConfig} />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("plan_type")}
                    role="button"
                    aria-label={`Sort by plan type, currently sorted ${
                      sortConfig.field === "plan_type"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Plan Type
                      <SortIcons field="plan_type" sortConfig={sortConfig} />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("period_type")}
                    role="button"
                    aria-label={`Sort by period type, currently sorted ${
                      sortConfig.field === "period_type"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Period Type
                      <SortIcons field="period_type" sortConfig={sortConfig} />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("value_type")}
                    role="button"
                    aria-label={`Sort by value type, currently sorted ${
                      sortConfig.field === "value_type"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Value Type
                      <SortIcons field="value_type" sortConfig={sortConfig} />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("fixed_value")}
                    role="button"
                    aria-label={`Sort by fixed value, currently sorted ${
                      sortConfig.field === "fixed_value"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Incentive Value
                      <SortIcons field="fixed_value" sortConfig={sortConfig} />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("currency_code")}
                    role="button"
                    aria-label={`Sort by currency code, currently sorted ${
                      sortConfig.field === "currency_code"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Currency Code
                      <SortIcons
                        field="currency_code"
                        sortConfig={sortConfig}
                      />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="text-center cursor-pointer"
                    onClick={() => handleSort("is_active")}
                    role="button"
                    aria-label={`Sort by status, currently sorted ${
                      sortConfig.field === "is_active"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center justify-content-center">
                      Status
                      <SortIcons field="is_active" sortConfig={sortConfig} />
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="cursor-pointer"
                    onClick={() => handleSort("created_at")}
                    role="button"
                    aria-label={`Sort by created date, currently sorted ${
                      sortConfig.field === "created_at"
                        ? sortConfig.direction
                        : "none"
                    }`}
                  >
                    <div className="d-flex align-items-center">
                      Created Date
                      <SortIcons field="created_at" sortConfig={sortConfig} />
                    </div>
                  </th>
                  <th scope="col" className="text-center">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {incentivePlans.map((plan, index) => (
                  <tr key={plan.id || `row-${index}`}>
                    <td>
                      {(pagination.currentPage - 1) * pagination.perPage +
                        index +
                        1}
                    </td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {plan.plan_name}
                      </span>
                    </td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {plan.plan_type}
                      </span>
                    </td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {plan.period_type.toLowerCase()}
                      </span>
                    </td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {plan.value_type.toLowerCase()}
                      </span>
                    </td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {plan.fixed_value}
                      </span>
                    </td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {plan.currency_code}
                      </span>
                    </td>
                    <td className="">
                      <StatusBadge status={plan.is_active} />
                    </td>
                    <td>
                      <span className="text-md mb-0 fw-normal text-secondary-light">
                        {plan.display_created_at}
                      </span>
                    </td>
                    <td className="text-center">
                      <div className="d-flex align-items-center gap-10 justify-content-center">
                        {hasPermission("add_incentive_assignment") &&
                          plan.id &&
                          !String(plan.id).startsWith("temp_") && (
                            <button
                              type="button"
                              onClick={() => handleAssignIncentive(plan)}
                              className="bg-primary-50 text-primary-600 bg-hover-primary-100 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                              title={`Assign ${plan.plan_name}`}
                              aria-label={`Assign incentive plan ${plan.plan_name}`}
                            >
                              <Icon
                                icon="fa6-solid:hand-holding-dollar"
                                className="menu-icon"
                              />
                            </button>
                          )}
                        {hasPermission("update_incentive") &&
                          plan.id &&
                          !String(plan.id).startsWith("temp_") && (
                            <button
                              type="button"
                              onClick={() => handleEdit(plan)}
                              className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                              title={`Edit ${plan.plan_name}`}
                              aria-label={`Edit incentive plan ${plan.plan_name}`}
                            >
                              <Icon icon="lucide:edit" className="menu-icon" />
                            </button>
                          )}
                        {hasPermission("delete_incentive") &&
                          plan.id &&
                          !String(plan.id).startsWith("temp_") && (
                            <button
                              type="button"
                              onClick={() =>
                                handleDeleteClick(plan.id, plan.plan_name)
                              }
                              className="bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                              title={`Delete ${plan.plan_name}`}
                              aria-label={`Delete incentive plan ${plan.plan_name}`}
                            >
                              <Icon
                                icon="fluent:delete-24-regular"
                                className="menu-icon"
                              />
                            </button>
                          )}
                        {(!plan.id || String(plan.id).startsWith("temp_")) && (
                          <span className="text-neutral-900 small">
                            Invalid ID
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {loading && (
          <div
            className="position-absolute top-50 start-50 translate-middle d-flex justify-content-center align-items-center"
            style={{
              zIndex: 10,
              borderRadius: "10px",
              padding: "20px",
            }}
          >
            <LoadingComponent />
          </div>
        )}
      </CardContainer>

      {/* 👈 EXISTING: Delete Modal */}
      <div
        className={`modal fade ${showDeleteModal ? "show d-block" : ""}`}
        tabIndex="-1"
        role="dialog"
        style={{ display: showDeleteModal ? "block" : "none",backgroundColor: showDeleteModal ? "rgba(0,0,0,0.5)" : "" 
 }}
      >
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Confirm Deletion</h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleDeleteCancel}
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <p className="text-neutral-900">
                Are you sure you want to delete{" "}
                <strong>{planToDelete?.name || "this incentive plan"}</strong>?
              </p>
              <p className="text-neutral-900 small">
                This action cannot be undone.
              </p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleDeleteCancel}
                aria-label="Cancel deletion"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDeleteConfirm}
                aria-label={`Confirm delete incentive plan ${
                  planToDelete?.name || ""
                }`}
                disabled={loading}
              >
                {loading ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      </div>
      {showDeleteModal && <div className="no-modal fade show"></div>}

     {/* REPLACE WITH THIS NEW CODE */}
<InfoModal
  isOpen={showInfoModal}
  onClose={handleInfoClose}
  title="Incentive Plan Management - Complete Overview"
>
  <p className="text-neutral-900 mb-4">
    This page serves as the <strong>central dashboard</strong> for managing all <strong>Staff Incentive Plans</strong>. 
    It allows administrators to define how much incentive (in points or currency) employees earn based on performance, on a daily, weekly, or monthly basis.
  </p>

  <h6 className="fw-bold text-primary-600 mb-3">Core Purpose</h6>
  <ul className="list-group list-group-flush mb-4">
    <li className="list-group-item">Create, view, edit, and delete incentive plans</li>
    <li className="list-group-item">Each plan defines the exact incentive amount and payout frequency</li>
    <li className="list-group-item">Plans must be <strong>assigned</strong> to staff (via branches, locations, and workflows) to become active</li>
    <li className="list-group-item">Drives employee motivation through structured, performance-linked rewards</li>
  </ul>

  <h6 className="fw-bold text-primary-600 mb-3">Add New Plan – Available Fields</h6>
  <p className="small text-neutral-600 mb-2">When clicking "Add Plan", the following configuration options are available:</p>
  <ul className="list-group list-group-flush mb-4">
    <li className="list-group-item"><strong>Plan Name</strong> – Unique identifier (e.g., "Monthly Sales Champion Bonus")</li>
    <li className="list-group-item"><strong>Period Type</strong> – Daily | Weekly | Monthly</li>
    <li className="list-group-item"><strong>Value Type</strong> – Points or Currency</li>
    <li className="list-group-item"><strong>Incentive Value</strong> – Fixed amount or percentage (e.g., $500 or 5% of revenue)</li>
    <li className="list-group-item"><strong>Currency Code</strong> – Required if Value Type is Currency</li>
    <li className="list-group-item"><strong>Minimum Redeemable Value</strong> – Threshold before payout is allowed</li>
    <li className="list-group-item"><strong>Status</strong> – Active / Inactive</li>
  </ul>

  <h6 className="fw-bold text-primary-600 mb-3">Three Action Buttons (Per Plan)</h6>
  <ul className="list-group list-group-flush mb-4">
    <li className="list-group-item">
      <strong>Assign (Blue Icon)</strong><br/>
      <span className="text-success-600 fw-bold">Most Important Action</span><br/>
      Opens assignment panel to link the plan to:<br/>
      → Branches | Locations | Workflows | Specific Incentive Plan<br/>
      <small className="text-danger-600">Requires permission: add_incentive_assignment</small>
    </li>
    <li className="list-group-item">
      <strong>Edit (Green Pencil)</strong><br/>
      Modify plan name, value, period, or rules
    </li>
    <li className="list-group-item">
      <strong>Delete (Red Trash)</strong><br/>
      Permanently remove the plan (only if not actively assigned or redeemed)
    </li>
  </ul>

  <div className="alert alert-info small mt-4">
    <strong>Note:</strong><br/>
    • Status filter dropdown is not yet implemented (coming in future update)<br/>
    • Plans with missing or temporary IDs ("temp_") will have actions disabled – refresh page to resolve<br/>
    • Creating a plan alone does nothing — it must be <strong>assigned</strong> to take effect
  </div>

  <p className="text-neutral-900 mt-4 fw-bold text-primary">
    Pro Tip: Always create a strong plan → Immediately click the Assign button → Start motivating your team!
  </p>
  <p className="text-neutral-900 small">
    Business Impact: Every incentive plan is a strategic tool for boosting performance, retention, and employee satisfaction.
  </p>
</InfoModal>
    </>
  );
};

export default IncentivePlan;